// src/gestion-logistics/components/ShipmentList.tsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  Button,
  Space,
  Popconfirm,
  Card,
  Typography,
  Tag,
  Tooltip,
} from "antd";
import {
  PlusOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  DollarOutlined,
} from "@ant-design/icons";
import type { ColumnType } from "antd/es/table";
import { useShipments } from "../hooks/useShipments";
import { useLogisticsPermissions } from "../hooks/useLogisticsPermissions";
import { Shipment, ShipmentFilters as Filters } from "../types/shipment.types";
import { useSelection } from "../../SelectionContext";
import ShipmentFilters from "./ShipmentFilters";
import ShipmentFormModal from "./ShipmentFormModal";
import StatusBadge from "./StatusBadge";
import TransportModeIcon from "./TransportModeIcon";
import dayjs from "dayjs";

const { Title } = Typography;

/**
 * Composant liste des dossiers d'expédition
 * Avec filtres, pagination, et actions CRUD
 */
const ShipmentList: React.FC = () => {
  const navigate = useNavigate();
  const {
    shipments,
    loading,
    pagination,
    fetchShipments,
    deleteShipment,
  } = useShipments();

  const permissions = useLogisticsPermissions();
  const { selectedCompany } = useSelection();

  const [filters, setFilters] = useState<Filters>({});
  const [formModalVisible, setFormModalVisible] = useState(false);
  const [selectedShipment, setSelectedShipment] = useState<Shipment | null>(null);
  const [formMode, setFormMode] = useState<"create" | "edit">("create");
  const [expandedRowKeys, setExpandedRowKeys] = useState<number[]>([]);

  // Charger les dossiers uniquement si une entreprise est sélectionnée
  useEffect(() => {
    if (selectedCompany) {
      fetchShipments(filters, pagination.page);
    }
  }, [filters, selectedCompany]);

  const handleFilterChange = (newFilters: Filters) => {
    setFilters(newFilters);
  };

  const handleResetFilters = () => {
    setFilters({});
  };

  const handlePageChange = (page: number) => {
    fetchShipments(filters, page);
  };

  const handleDelete = async (id: number) => {
    await deleteShipment(id);
  };

  const handleView = (record: Shipment) => {
    // Naviguer vers la page de détails au lieu d'ouvrir un modal
    navigate(`/logistics/shipment/${record.id}`);
  };

  const handleEdit = (record: Shipment) => {
    setSelectedShipment(record);
    setFormMode("edit");
    setFormModalVisible(true);
  };

  const handleCreate = () => {
    setSelectedShipment(null);
    setFormMode("create");
    setFormModalVisible(true);
  };

  const handleCloseFormModal = () => {
    setFormModalVisible(false);
    setSelectedShipment(null);
    // Rafraîchir automatiquement la liste après fermeture du modal
    fetchShipments();
  };

  const formatCurrency = (amount: number | string | null, symbol?: string) => {
    if (amount === null || amount === undefined) return `0 ${symbol || ""}`;
    const numericAmount = typeof amount === "string" ? parseFloat(amount) : amount;
    if (isNaN(numericAmount)) return `0 ${symbol || ""}`;
    return `${numericAmount.toLocaleString("fr-FR", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })} ${symbol || ""}`;
  };

  const formatProfitMargin = (revenue: number | string, cost: number | string, margin: number | string) => {
    const numRevenue = typeof revenue === "string" ? parseFloat(revenue) : revenue;
    const numMargin = typeof margin === "string" ? parseFloat(margin) : margin;
    const percentage = numRevenue > 0 ? ((numMargin / numRevenue) * 100).toFixed(1) : "0.0";
    const isPositive = numMargin >= 0;

    return (
      <Space direction="vertical" size={0}>
        <span style={{ color: isPositive ? "green" : "red", fontWeight: "bold" }}>
          {formatCurrency(numMargin)}
        </span>
        <span style={{ fontSize: "11px", color: "#999" }}>
          ({percentage}%)
        </span>
      </Space>
    );
  };

  // Configuration de l'expansion pour afficher les conteneurs et colis
  const expandable = {
    expandedRowRender: (record: Shipment) => {
      const containers = record.containers || [];
      const packages = record.packages || [];

      // Séparer les colis : ceux liés à un conteneur et ceux sans conteneur
      const packagesWithoutContainer = packages.filter((p) => !p.container_id);

      // Si pas de données, afficher un message
      if (containers.length === 0 && packages.length === 0) {
        return (
          <div style={{ padding: "16px", color: "#999", fontStyle: "italic" }}>
            Aucun conteneur ou colis enregistré pour ce dossier.
          </div>
        );
      }

      return (
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          {/* Tableau des conteneurs avec colis imbriqués */}
          {containers.length > 0 && (
            <div>
              <Title level={5} style={{ marginBottom: 8 }}>
                Conteneurs ({containers.length})
              </Title>
              <Table
                dataSource={containers}
                pagination={false}
                size="small"
                bordered
                rowKey={(c) => c.id || Math.random()}
                expandable={{
                  expandedRowRender: (container) => {
                    // Filtrer les colis de ce conteneur
                    const containerPackages = packages.filter(
                      (p) => p.container_id === container.id
                    );

                    if (containerPackages.length === 0) {
                      return (
                        <div
                          style={{
                            padding: "8px 16px",
                            color: "#999",
                            fontStyle: "italic",
                            fontSize: "12px",
                          }}
                        >
                          Aucun colis dans ce conteneur
                        </div>
                      );
                    }

                    return (
                      <Table
                        dataSource={containerPackages}
                        pagination={false}
                        size="small"
                        showHeader={true}
                        rowKey={(p) => p.id || Math.random()}
                        columns={[
                          {
                            title: "N° Colis",
                            dataIndex: "package_number",
                            key: "package_number",
                            width: 120,
                          },
                          {
                            title: "Description",
                            dataIndex: "description",
                            key: "description",
                            ellipsis: true,
                          },
                          {
                            title: "Poids (kg)",
                            dataIndex: "weight",
                            key: "weight",
                            width: 100,
                            align: "right",
                            render: (w) =>
                              w ? Number(w).toLocaleString("fr-FR") : "-",
                          },
                          {
                            title: "Volume (m³)",
                            dataIndex: "volume",
                            key: "volume",
                            width: 100,
                            align: "right",
                            render: (v) => (v ? Number(v).toFixed(3) : "-"),
                          },
                          {
                            title: "Propriétaire",
                            dataIndex: "owner_client_name",
                            key: "owner_client_name",
                            width: 150,
                            ellipsis: true,
                          },
                          {
                            title: "Dangereux",
                            dataIndex: "is_dangerous",
                            key: "is_dangerous",
                            width: 100,
                            align: "center",
                            render: (isDangerous, p) =>
                              isDangerous ? (
                                <Tag color="red">
                                  Oui {p.un_number ? `(${p.un_number})` : ""}
                                </Tag>
                              ) : (
                                <span style={{ color: "#999" }}>Non</span>
                              ),
                          },
                        ]}
                      />
                    );
                  },
                  rowExpandable: (container) => {
                    const containerPackages = packages.filter(
                      (p) => p.container_id === container.id
                    );
                    return containerPackages.length > 0;
                  },
                }}
                columns={[
                  {
                    title: "N° Conteneur",
                    dataIndex: "container_number",
                    key: "container_number",
                    width: 150,
                  },
                  {
                    title: "Type",
                    key: "type",
                    width: 100,
                    render: (_, c) => {
                      const type = c.size_feet ? `${c.size_feet}'` : "";
                      const height = c.height_type === "high_cube" ? " HC" : "";
                      return type + height || "-";
                    },
                  },
                  {
                    title: "N° Scellé",
                    dataIndex: "seal_number",
                    key: "seal_number",
                    width: 120,
                  },
                  {
                    title: "Poids (kg)",
                    dataIndex: "weight",
                    key: "weight",
                    width: 100,
                    align: "right",
                    render: (w) => (w ? Number(w).toLocaleString("fr-FR") : "-"),
                  },
                  {
                    title: "Volume (m³)",
                    dataIndex: "volume",
                    key: "volume",
                    width: 100,
                    align: "right",
                    render: (v) => (v ? Number(v).toFixed(2) : "-"),
                  },
                  {
                    title: "Colis",
                    key: "packages_count",
                    width: 80,
                    align: "center",
                    render: (_, container) => {
                      const count = packages.filter(
                        (p) => p.container_id === container.id
                      ).length;
                      return count > 0 ? (
                        <Tag color="green">{count}</Tag>
                      ) : (
                        <span style={{ color: "#999" }}>0</span>
                      );
                    },
                  },
                  {
                    title: "Reefer",
                    dataIndex: "is_reefer",
                    key: "is_reefer",
                    width: 80,
                    align: "center",
                    render: (isReefer, c) =>
                      isReefer ? (
                        <Tag color="blue">
                          Oui {c.temperature ? `(${c.temperature}°C)` : ""}
                        </Tag>
                      ) : (
                        <span style={{ color: "#999" }}>Non</span>
                      ),
                  },
                ]}
              />
            </div>
          )}

          {/* Tableau des colis sans conteneur (en vrac) */}
          {packagesWithoutContainer.length > 0 && (
            <div>
              <Title level={5} style={{ marginBottom: 8 }}>
                Colis en vrac ({packagesWithoutContainer.length})
              </Title>
              <Table
                dataSource={packagesWithoutContainer}
                pagination={false}
                size="small"
                bordered
                rowKey={(p) => p.id || Math.random()}
                columns={[
                  {
                    title: "N° Colis",
                    dataIndex: "package_number",
                    key: "package_number",
                    width: 120,
                  },
                  {
                    title: "Description",
                    dataIndex: "description",
                    key: "description",
                    ellipsis: true,
                  },
                  {
                    title: "Poids (kg)",
                    dataIndex: "weight",
                    key: "weight",
                    width: 100,
                    align: "right",
                    render: (w) => (w ? Number(w).toLocaleString("fr-FR") : "-"),
                  },
                  {
                    title: "Volume (m³)",
                    dataIndex: "volume",
                    key: "volume",
                    width: 100,
                    align: "right",
                    render: (v) => (v ? Number(v).toFixed(3) : "-"),
                  },
                  {
                    title: "Propriétaire",
                    dataIndex: "owner_client_name",
                    key: "owner_client_name",
                    width: 150,
                    ellipsis: true,
                  },
                  {
                    title: "Dangereux",
                    dataIndex: "is_dangerous",
                    key: "is_dangerous",
                    width: 100,
                    align: "center",
                    render: (isDangerous, p) =>
                      isDangerous ? (
                        <Tag color="red">
                          Oui {p.un_number ? `(${p.un_number})` : ""}
                        </Tag>
                      ) : (
                        <span style={{ color: "#999" }}>Non</span>
                      ),
                  },
                ]}
              />
            </div>
          )}
        </Space>
      );
    },
    onExpand: (expanded: boolean, record: Shipment) => {
      if (expanded) {
        setExpandedRowKeys((prev) => [...prev, record.id]);
      } else {
        setExpandedRowKeys((prev) => prev.filter((key) => key !== record.id));
      }
    },
    expandedRowKeys: expandedRowKeys,
  };

  const columns: ColumnType<Shipment>[] = [
    {
      title: "N° Dossier",
      dataIndex: "shipment_number",
      key: "shipment_number",
      width: 140,
      fixed: "left",
      render: (text: string, record: Shipment) => (
        <a
          onClick={() => handleView(record)}
          style={{
            fontWeight: "bold",
            color: "#1890ff",
            cursor: "pointer",
            textDecoration: "underline",
          }}
        >
          {text}
        </a>
      ),
    },
    {
      title: "Client",
      dataIndex: "client_name",
      key: "client_name",
      width: 200,
      ellipsis: true,
    },
    {
      title: "Origine → Destination",
      key: "route",
      width: 250,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <span style={{ fontSize: "12px" }}>
            <strong>De:</strong> {record.origin_city || record.origin_country || "N/A"}
          </span>
          <span style={{ fontSize: "12px" }}>
            <strong>À:</strong> {record.destination_city || record.destination_country || "N/A"}
          </span>
        </Space>
      ),
    },
    {
      title: "Transport",
      dataIndex: "transport_mode",
      key: "transport_mode",
      width: 120,
      align: "center",
      render: (mode) => <TransportModeIcon mode={mode} showLabel={false} />,
    },
    {
      title: "Statut",
      dataIndex: "status",
      key: "status",
      width: 130,
      align: "center",
      render: (status) => <StatusBadge status={status} size="small" />,
    },
    {
      title: "Dates",
      key: "dates",
      width: 150,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          {record.etd && (
            <Tooltip title="ETD - Estimated Time of Departure">
              <span style={{ fontSize: "11px" }}>
                ETD: {dayjs(record.etd).format("DD/MM/YY")}
              </span>
            </Tooltip>
          )}
          {record.eta && (
            <Tooltip title="ETA - Estimated Time of Arrival">
              <span style={{ fontSize: "11px" }}>
                ETA: {dayjs(record.eta).format("DD/MM/YY")}
              </span>
            </Tooltip>
          )}
          {!record.etd && !record.eta && <span style={{ color: "#999" }}>-</span>}
        </Space>
      ),
    },
    {
      title: "Coûts",
      dataIndex: "total_cost",
      key: "total_cost",
      width: 120,
      align: "right",
      render: (cost, record) => (
        <span style={{ color: "#d46b08" }}>
          {formatCurrency(cost, record.currency_symbol)}
        </span>
      ),
    },
    {
      title: "Revenus",
      dataIndex: "total_revenue",
      key: "total_revenue",
      width: 120,
      align: "right",
      render: (revenue, record) => (
        <span style={{ color: "#389e0d" }}>
          {formatCurrency(revenue, record.currency_symbol)}
        </span>
      ),
    },
    {
      title: "Marge",
      key: "profit_margin",
      width: 130,
      align: "right",
      render: (_, record) =>
        formatProfitMargin(
          record.total_revenue,
          record.total_cost,
          record.profit_margin
        ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 150,
      fixed: "right",
      align: "center",
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Voir détails">
            <Button
              type="text"
              icon={<EyeOutlined />}
              size="small"
              onClick={() => handleView(record)}
            />
          </Tooltip>

          {permissions.canCreateShipments && (
            <Tooltip title="Modifier">
              <Button
                type="text"
                icon={<EditOutlined />}
                size="small"
                onClick={() => handleEdit(record)}
              />
            </Tooltip>
          )}

          {permissions.canDeleteShipments && (
            <Popconfirm
              title="Supprimer ce dossier ?"
              description="Cette action est irréversible. Toutes les données associées seront supprimées."
              onConfirm={() => handleDelete(record.id)}
              okText="Supprimer"
              cancelText="Annuler"
              okButtonProps={{ danger: true }}
            >
              <Tooltip title="Supprimer">
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  size="small"
                />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card
        title={
          <Space align="center">
            <Title level={4} style={{ margin: 0 }}>
              Dossiers d'Expédition
            </Title>
            <Tag color="blue">{pagination.total || 0} dossier(s)</Tag>
          </Space>
        }
        extra={
          permissions.canCreateShipments && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreate}
            >
              Nouveau Dossier
            </Button>
          )
        }
      >
        <ShipmentFilters
          filters={filters}
          onFilterChange={handleFilterChange}
          onReset={handleResetFilters}
          loading={loading}
        />

        <Table
          columns={columns}
          dataSource={shipments}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1400 }}
          pagination={{
            current: pagination.page,
            pageSize: pagination.limit,
            total: pagination.total,
            showSizeChanger: false,
            showTotal: (total) => `Total: ${total} dossier(s)`,
            onChange: handlePageChange,
          }}
          expandable={expandable}
          size="small"
        />

        <ShipmentFormModal
          visible={formModalVisible}
          onClose={handleCloseFormModal}
          shipment={selectedShipment}
          mode={formMode}
        />
      </Card>
    </div>
  );
};

export default ShipmentList;
